'use client'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Container from '@mui/material/Container'
import { memo, useState } from 'react'
import logo from 'public/logo.png'
import Image from 'next/image'
import { BORDER_GREY, WHITE } from '@/constants/colors'
import {
  DownloadRounded,
  HelpRounded,
  SupportAgent,
  FacebookOutlined,
  Instagram,
  Pinterest,
  Email,
  Security,
  Close,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Badge,
  badgeClasses,
  Button,
  Drawer,
  LinearProgress,
  Stack,
  styled,
  Tooltip,
  Typography,
  Link as MuiLink,
} from '@mui/material'
import { useSettings } from '@/providers/SettingsProvider'
// import { useDownloads } from '@/providers/DownloadsProvider'
import { Link } from '@/i18n/routing'
import dynamic from 'next/dynamic'
import SocialLinks from './SocialLinks'
import FeedbackConnect from '../FeedbackConnect'
import { useLocale, useTranslations } from 'next-intl'
import { useLogger } from '@/lib/axiom/client'
import { getCookie } from '@/utils/Utils'
import { SITE_ORIGIN } from '@/constants/site'


const DownloadBadge = styled(Badge)`
  & .${badgeClasses.badge} {
    top: -12px;
    right: -6px;
  }
`

// const DownloadsDrawer = dynamic(() => import('./DownloadsDrawer'), { ssr: false })

const TopAppBar = () => {
  const [open, setOpen] = useState<boolean>(false)
  const [openHelpDrawer, setOpenHelpDrawer] = useState<boolean>(false)
  const { showProgress } = useSettings()
  const t = useTranslations('TOP_APP_BAR')
  const logger = useLogger().with({ userId: getCookie('mbm-user-id') })
  // const { downloads, refreshDownloads } = useDownloads()

  // const handleOpenDrawer = () => {
  // refreshDownloads()
  // setOpen(true)
  // }

  const locale = useLocale()
  const homeUrl = locale === 'en' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}/${locale}/`

  return (
    <>
      <AppBar position="fixed" sx={{ background: WHITE }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box
              sx={{
                width: { xs: '120px', md: '180px' },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <h1 style={{ margin: 0, padding: 0, display: 'flex', alignItems: 'center', fontSize: 0 }}>
                <a href={homeUrl} style={{ display: 'flex', alignItems: 'center' }}>
                  <Image
                    src={logo}
                    priority
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      marginRight: 1,
                    }}
                    alt="Marriage Biodata Maker"
                  />
                </a>
              </h1>
            </Box>

            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                ml: 'auto'
              }}>
              {/* <IconButton size="medium" onClick={handleOpenDrawer} color="primary" sx={{ border: `1px solid ${BORDER_GREY}` }}>
                <DownloadRounded />
                <DownloadBadge badgeContent={downloads.length} color="primary" overlap="circular" showZero />
              </IconButton> */}
              <Button size="medium" onClick={() => {
                logger.info('Support button clicked')
                setOpenHelpDrawer(true)
              }} color="primary" variant="outlined" startIcon={<SupportAgent />}>
                {t('SUPPORT')}
              </Button>
            </Stack>
          </Toolbar>
        </Container>
        {showProgress && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        )}
      </AppBar>
      <Drawer
        open={openHelpDrawer}
        onClose={() => setOpenHelpDrawer(false)}
        anchor="bottom"
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
              p: 3,
            },
          },
        }}
      >
        <IconButton onClick={() => setOpenHelpDrawer(false)} sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Close />
        </IconButton>
        <Box sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgent color="primary" />
              <Typography variant="h5" sx={{
                fontWeight: "bold"
              }}>
                {t('SUPPORT_AND_TRUST')}
              </Typography>
            </Box>

            <Alert severity="success" icon={<Security />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <AlertTitle>{t('PAYMENT_SECURITY_TITLE')}</AlertTitle>
              {t('PAYMENT_SECURITY_DESC')}
            </Alert>

            <Box>
              <Typography variant="h6" gutterBottom>
                {t('CONTACT_US')}
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email color="primary" />
                  <Typography variant="body1" sx={{
                    fontSize: 14
                  }}>
                    {t('EMAIL')}{' '}
                    <MuiLink href="mailto:contact@marriagebiodatamaker.app" color="inherit">
                      contact@marriagebiodatamaker.app
                    </MuiLink>
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" gutterBottom sx={{
                    color: "text.secondary"
                  }}>
                    {t('SOCIAL_MEDIA_DESC')}
                  </Typography>
                  <SocialLinks />
                </Box>
              </Stack>
            </Box>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{
                fontWeight: "medium"
              }}>
                {t('WHY_TRUST_US')}
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  • {t('TRUST_POINTS.SECURE_PAYMENT')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  • {t('TRUST_POINTS.CUSTOMER_SUPPORT')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  • {t('TRUST_POINTS.TRANSPARENT_PRICING')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  • {t('TRUST_POINTS.MONEY_BACK')}
                </Typography>
              </Stack>
            </Box>

            <FeedbackConnect />
          </Stack>
        </Box>
      </Drawer>
      {/* <DownloadsDrawer open={open} onClose={() => setOpen(false)} /> */}
    </>
  );
}

export default memo(TopAppBar)
